import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import { useTreeMutations } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks/TreeMutations/useTreeMutations'
import {
    useNotification,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types/types'
import { getRootArticleContentItemIds } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useShallowSelector } from '../../../Workspace2Store'
import { ContentItemViewNodesType } from '../../../types'
import {
    preventEventDefault,
    tryGetShortcutKey,
} from '../../../utils/shortcutKeyUtils'
import { usePromoteDemoteContentMutation } from '../api/mutations/promoteDemoteContent'
import {
    INCONSISTENT_PARAGRAPH_LEVEL_ERROR,
    NOT_FOUND,
    NO_ACTIVE_ID,
    PromoteDemoteActions,
} from '../constants'
import {
    usePromoteDemoteSelector,
    usePromoteDemoteStore,
} from '../store/PromoteDemoteStore'
import { PromoteDemoteAction } from '../types'
import { wouldExceedDepthLimit } from '../utils'
import { useFocusRestoration } from './useFocusRestoration'
import { usePromoteDemoteService } from './usePromoteDemoteService'

/**
 * Hook that encapsulates promote/demote functionality for text content elements.
 *
 * @returns An object containing state and functions for promote/demote operations
 */
export const usePromoteDemote = () => {
    const { buildInputVariables } = usePromoteDemoteService()
    const { executeWithOptimistic } = useTransactionManager()
    const { promoteDemoteContentItem } = usePromoteDemoteContentMutation()
    const { reRenderTree } = useTreeMutations()
    const { restoreFocus } = useFocusRestoration()
    // Captures the active item ID so the fetch callback can restore focus after
    // reRenderTree, even though resetPromoteDemote() has already cleared the store.
    const pendingFocusIdRef = useRef<string | undefined>()
    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })
    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            const updatedNodes = await onContentViewFetchSuccess(
                response,
                [],
                true
            )
            if (updatedNodes) {
                reRenderTree({ updatedContentItemViewNodes: updatedNodes })
                if (pendingFocusIdRef.current) {
                    restoreFocus(pendingFocusIdRef.current)
                    pendingFocusIdRef.current = undefined
                }
            }
        },
    })
    const notification = useNotification()
    const {
        isContentTeamFromStudioPage,
        isEditingPlainText,
        contentItemViewNodes,
        newParagraphToAdd,
        treeData,
    } = useShallowSelector((s) => ({
        isContentTeamFromStudioPage: s.isContentTeamFromStudioPage,
        isEditingPlainText: s.isEditingPlainText,
        contentItemViewNodes: s.contentItemViewNodes,
        newParagraphToAdd: s.textContentElementToAdd,
        treeData: s.treeData,
    }))

    const {
        canBePromoted,
        canBeDemoted,
        contentItemToPromoteDemote: activeItem,
        isMutationInFlight,
        setMutationInFlight,
        setContentItemToPromoteDemote,
        resetPromoteDemoteState: resetPromoteDemote,
    } = usePromoteDemoteSelector((s) => ({
        canBePromoted: s.canBePromoted,
        canBeDemoted: s.canBeDemoted,
        contentItemToPromoteDemote: s.contentItemToPromoteDemote,
        isMutationInFlight: s.isMutationInFlight,
        setMutationInFlight: s.setMutationInFlight,
        setContentItemToPromoteDemote: s.setContentItemToPromoteDemote,
        resetPromoteDemoteState: s.resetPromoteDemoteState,
    }))

    const isCreatingNewParagraph = useMemo(() => Boolean(newParagraphToAdd), [
        newParagraphToAdd,
    ])
    const canPromote =
        !isCreatingNewParagraph &&
        !isMutationInFlight &&
        canBePromoted(isContentTeamFromStudioPage)
    const canDemote =
        !isCreatingNewParagraph &&
        !isMutationInFlight &&
        canBeDemoted(isContentTeamFromStudioPage)

    // Register content for promote/demote operations
    const register = useCallback(
        (
            contentItem: Partial<TextContentElement>,
            isInProject: boolean = false,
            contentItemViewNodes: Partial<ContentItemViewNodesType>[] = []
        ) => {
            setContentItemToPromoteDemote({
                contentItemToPromoteDemote: contentItem,
                isInProject,
                contentItemViewNodes,
            })
        },
        [setContentItemToPromoteDemote]
    )

    // handle mutation request creation and execution
    const handlePromoteDemoteMutation = useCallback(
        async (action: PromoteDemoteAction) => {
            const activeItemId = activeItem?.id

            if (
                isCreatingNewParagraph ||
                usePromoteDemoteStore.getState().isMutationInFlight
            )
                return
            if (!activeItemId)
                return notification.error({
                    message: `${NO_ACTIVE_ID} ${action}`,
                })
            if (
                wouldExceedDepthLimit(
                    activeItemId,
                    action,
                    treeData,
                    5,
                    contentItemViewNodes ?? []
                )
            )
                return notification.error(INCONSISTENT_PARAGRAPH_LEVEL_ERROR)

            const variables = buildInputVariables(action)
            if (!variables)
                return notification.error({
                    message: `${NOT_FOUND} ${action}`,
                })

            // Store the ID so the fetch callback can restore focus after reRenderTree.
            // resetPromoteDemote() clears the store before the fetch completes, so we
            // cannot read activeItem from the selector at callback time.
            pendingFocusIdRef.current = activeItemId

            // Capture current nodes to scope the re-fetch to the affected article
            const currentNodes = contentItemViewNodes

            setMutationInFlight(true)
            executeWithOptimistic({
                fallbackState: contentItemViewNodes,
                asyncAction: () => promoteDemoteContentItem({ variables }),
                successCbFn: async () => {
                    resetPromoteDemote()

                    // Re-fetch only the article containing the moved item so that
                    // dtvItemId values are authoritative for subsequent mutations.
                    // Keep isMutationInFlight=true until the tree is fully rebuilt
                    // so no further operations can fire against stale state.
                    const rootArticleContentItemIds = getRootArticleContentItemIds(
                        currentNodes,
                        [activeItemId]
                    )
                    try {
                        await getContentItemViewNodesWithApolloClient(
                            rootArticleContentItemIds
                        )
                    } catch {
                        // re-fetch is best-effort; tree state persists until next mutation
                    } finally {
                        // If the fetch failed the callback never ran, so the ref
                        // was not cleared there. Clear it here in all cases so it
                        // never leaks into a subsequent operation.
                        pendingFocusIdRef.current = undefined
                        setMutationInFlight(false)
                    }
                },
                errorCbFn: () => {
                    pendingFocusIdRef.current = undefined
                    setMutationInFlight(false)
                },
            })
        },
        [
            executeWithOptimistic,
            promoteDemoteContentItem,
            buildInputVariables,
            activeItem,
            contentItemViewNodes,
            isCreatingNewParagraph,
            treeData,
            notification,
            setMutationInFlight,
            getContentItemViewNodesWithApolloClient,
        ]
    )

    // handle keyboard events for promote/demote shortcuts
    const handlePromoteDemoteShortcutKey = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            const { Promote, Demote } = PromoteDemoteActions

            if (tryGetShortcutKey(Promote, event) && canPromote) {
                // Prevent default Shift+Tab behavior to stop focus from moving
                preventEventDefault(event)
                handlePromoteDemoteMutation(Promote)
            } else if (tryGetShortcutKey(Demote, event) && canDemote) {
                // Prevent default Tab behavior to stop focus from moving to next element
                preventEventDefault(event)
                handlePromoteDemoteMutation(Demote)
            }
        },
        [canPromote, canDemote, handlePromoteDemoteMutation]
    )

    /**
     * side-effect: reset promote/demote state when nothing is selected
     * Toggles the Promote/Demote buttons on action bar
     */
    useEffect(() => {
        let resetId: NodeJS.Timeout

        if (!isEditingPlainText && activeItem) {
            resetId = setTimeout(() => {
                // reset means toggle the promote/demote buttons on action bar
                resetPromoteDemote()
            }, 100) // 100ms delay to ensure that the current item is evaluated by action bar before resetting the state
        }

        return () => clearTimeout(resetId)
    }, [isEditingPlainText, activeItem, resetPromoteDemote])

    return {
        canPromote,
        canDemote,
        isMutationInFlight,
        activeItem,
        register,
        handlePromoteDemoteShortcutKey,
        handlePromoteDemoteMutation,
        resetPromoteDemote,
    }
}
